import { Modal, Button } from "react-bootstrap";

// "How does this work?" modal. Three variants share most content via
// the Pipeline section; each gets a tailored What-you-are-looking-at
// intro:
//   - search: /leads/search inline-decision review of VOA candidates
//   - leads:  /leads promoted-leads list (review state + create-site)
//   - import: /leads/import CSV-driven bulk-import flow

export type HelpVariant = "leads" | "search" | "import";

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <section style={{ marginBottom: 14 }}>
    <h6 style={{ marginBottom: 4 }}>{title}</h6>
    <div style={{ fontSize: 13, lineHeight: 1.45, color: "#333" }}>
      {children}
    </div>
  </section>
);

const SharedPipeline: React.FC = () => (
  <>
    <Section title="Free Stage-1 enrichment (runs on every row)">
      <ul>
        <li>
          <strong>Mapbox geocoding</strong> — address → lat/lon. Free
          under 100k requests/month. Falls back to{" "}
          <strong>postcodes.io</strong> (postcode-centroid) when Mapbox
          confidence is LOW.
        </li>
        <li>
          <strong>Building polygons</strong> via our datasets-api
          (OpenStreetMap-derived). Top 5 candidates ranked by
          proximity + area-match + footprint size.
        </li>
        <li>
          <strong>HMLR title deeds</strong> via datasets-api (CCOD/OCOD
          public extracts). Top 3 deeds within 1km of the building,
          filtered to those whose title polygon overlaps the selected
          building polygon (centroid + vertex-in-polygon test, both
          directions).
        </li>
        <li>
          <strong>Companies House</strong> — active companies registered
          at the postcode. Dissolved / liquidated / removed companies
          are filtered out.
        </li>
        <li>
          <strong>OpenStreetMap</strong> (Overpass) — businesses tagged{" "}
          <code>operator</code> / <code>brand</code> /{" "}
          <code>name</code> near the building. Free, ODbL.
        </li>
      </ul>
    </Section>

    <Section title="Premium enrichment (operator-opt-in, ~$0.01/row)">
      <p>
        On a row the operator suspects the free path missed something,
        clicking <em>Enrich with paid APIs</em> (per-row or multi-select)
        adds:
      </p>
      <ul>
        <li>
          <strong>Google Places Text Search</strong> — sharpens the
          geocode where Mapbox missed (~8× more often pins the actual
          building on industrial-estate addresses).
        </li>
        <li>
          <strong>Google Places Nearby</strong> — finds operating brand
          tenants Companies House misses (Costco, B&amp;Q, etc.). Display
          names are stemmed to drop city / branch suffixes.
        </li>
      </ul>
      <p style={{ color: "#666", fontSize: 12 }}>
        Both fall under Google Maps Platform&rsquo;s shared $200/month
        credit (~20,000 rows of Places calls).
      </p>
    </Section>

    <Section title="Reliability + degraded data">
      <p>
        Every external integration sits behind a per-API circuit
        breaker. When an upstream rate-limits (429) or 5xx-storms,
        the worker stops calling it for the rest of the run rather
        than burning the Lambda budget on doomed retries.
      </p>
      <p>
        Affected rows surface a yellow{" "}
        <strong>&ldquo;Missing N source&rdquo;</strong> badge on{" "}
        <code>/leads/search</code> with a tooltip naming what failed.
        A banner above the results offers a one-click{" "}
        <em>Retry missing data</em> action that re-runs only the
        affected source against the affected rows — cheaper than a
        full per-row refetch.
      </p>
    </Section>

    <Section title="Already on the platform?">
      <p>
        Each landlord / tenant name is fuzzy-matched against existing
        platform Organisations. When a match is found the candidate
        carries an <code>orgId</code> so picking it binds to the
        existing org rather than creating a duplicate. The Buildings
        list also flags polygons that overlap an existing platform
        site.
      </p>
    </Section>
  </>
);

const SearchIntro: React.FC = () => (
  <Section title="What you’re looking at">
    <p>
      <code>/leads/search</code> is the inline-decision review surface
      for UK VOA Rating List candidates (warehouses + industrial +
      retail-warehouse subset). Filter by outcode + radius + min
      floor area; the backend runs the pipeline below.
    </p>
    <p>
      <strong>Cold-cache searches run in the background.</strong>{" "}
      Wide-radius or freshly-imported areas spawn a long-running
      operation (Step Functions for &gt;2,500 rows, sync Lambda
      otherwise) and return an operationKey immediately. Progress is
      visible on <code>/leads/search/status</code>; warm-cache
      searches return synchronously. The status page lists in-flight
      searches across all operators with progress + Cancel + Delete
      actions.
    </p>
    <p>
      Review each row in the right panel — pick a building polygon,
      pick a landlord, pick a tenant — then save. Saved rows can be
      promoted to Leads in bulk from the toolbar; the FE submits the
      currently-displayed selections (default-pick + any unsaved
      edits) so the resulting Lead reflects exactly what the operator
      saw on screen.
    </p>
  </Section>
);

const LeadsIntro: React.FC = () => (
  <Section title="What you’re looking at">
    <p>
      <code>/leads</code> is the working list of candidates we&rsquo;ve
      decided to pursue. Rows arrive either from a promoted
      <code>/leads/search</code> review, or from a{" "}
      <code>/leads/import</code> CSV upload. Each Lead carries the
      chosen polygon + landlord + tenant decisions and tags.
    </p>
    <p>
      The <strong>Lead</strong> column shows an overall confidence
      score (the minimum of the polygon, landlord and tenant per-
      element scores). Below 80% the bulk-create modal warns you to
      run premium enrichment first; <em>incomplete</em> means there&rsquo;s
      no polygon decision and the lead can&rsquo;t become a site without
      one.
    </p>
    <p>
      <strong>Bulk-create sites + orgs.</strong> Tick rows and click{" "}
      <em>Create N sites</em>. The preview shows what would be
      created (sites, new orgs, reused orgs, leads to skip + reason)
      with two-tier org dedupe: existing platform orgs are matched
      via fuzzy name, and within the batch two leads picking the
      same brand collapse to one new org rather than duplicating.
      Each created Site carries an <code>originatedFromLeadId</code>{" "}
      backlink so we can trace any deployed site back to its lead.
    </p>
    <p style={{ color: "#666", fontSize: 12 }}>
      The bulk-create runs as a long-running operation (one
      <code>create_site</code> call per lead; ~5s/lead through the
      full datasets-api decoration pipeline). Hard cap: 50 leads
      per operation. The modal polls progress and links each new
      site on completion.
    </p>
  </Section>
);

const ImportIntro: React.FC = () => (
  <Section title="What you’re looking at">
    <p>
      <code>/leads/import</code> bulk-imports leads from a customer CSV
      against the InRange RFI template (e.g.{" "}
      <code>HSBC_RFI_Template.xlsx</code>). Each row is enriched with
      the full free Stage-1 pipeline; address-quality is gated upfront
      so we don&rsquo;t enrich rows we can&rsquo;t locate.
    </p>
    <p>
      The CSV provides much richer per-row data than VOA: site
      name/ID, full address, ownership model (landlord name or
      &ldquo;Owner-occupied&rdquo;), lease expiry, building area, building
      use, half-hourly demand filename or fallback annual kWh, existing
      supplier + import tariff, and a customer-flagged priority. After
      import, the new rows show up at <code>/leads</code>.
    </p>
  </Section>
);

const ImportAddressGate: React.FC = () => (
  <Section title="CSV-only: Google Address Validation gate">
    <p>
      Before any other enrichment runs, every row is sent to{" "}
      <strong>Google Address Validation</strong> — a delivery-address
      verifier that grades how well Google could locate the address:
    </p>
    <ul>
      <li>
        <strong>Located the building</strong> /{" "}
        <strong>Located a unit within the building</strong> — full
        enrichment with a high-confidence badge.
      </li>
      <li>
        <strong>Street-level only</strong> — full enrichment but
        flagged for manual building-polygon verification.
      </li>
      <li>
        <strong>Couldn&rsquo;t recognise the address</strong> /{" "}
        <strong>Address incomplete</strong> — skip downstream
        enrichment, persist the verdict so the operator sees why on{" "}
        <code>/leads</code>.
      </li>
    </ul>
    <p style={{ color: "#666", fontSize: 12 }}>
      AV runs only on the CSV path. On <code>/leads/search</code> a
      2026-05 audit showed AV&rsquo;s lat/lons match Mapbox&rsquo;s at 22× the
      cost, so it was dropped from VOA enrichment.
    </p>
  </Section>
);

export const LeadsHelpModal: React.FC<{
  show: boolean;
  variant: HelpVariant;
  onHide: () => void;
}> = ({ show, variant, onHide }) => (
  <Modal show={show} onHide={onHide} size="lg" scrollable>
    <Modal.Header closeButton>
      <Modal.Title>
        {variant === "leads"
          ? "How /leads works"
          : variant === "import"
            ? "How /leads/import works"
            : "How /leads/search works"}
      </Modal.Title>
    </Modal.Header>
    <Modal.Body>
      {variant === "leads" && <LeadsIntro />}
      {variant === "search" && <SearchIntro />}
      {variant === "import" && (
        <>
          <ImportIntro />
          <ImportAddressGate />
        </>
      )}
      <SharedPipeline />
    </Modal.Body>
    <Modal.Footer>
      <Button variant="secondary" onClick={onHide}>
        Close
      </Button>
    </Modal.Footer>
  </Modal>
);

export default LeadsHelpModal;
